import { Frequency } from '@generated/graphql';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import {
  DEPOSIT_TRANSFER_TYPE,
  type DepositTransferType,
} from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { DepositStarting } from './depositSchedule';

type DepositFieldOptions = Record<string, Record<string, unknown>>;

type DepositData = Partial<TransferContextData> | undefined;

const requiredForTransferType =
  (transferType: DepositTransferType) => (data: DepositData) =>
    data?.type === transferType;

export const buildDepositFieldOptions = ({
  options,
  applicableData,
  starting,
  participantNumberEnabled,
  isMultiCurrencyInternalTransfer,
  hasAvailableCurrencies,
}: {
  options: DepositFieldOptions;
  applicableData?: Pick<TransferContextData, 'type'> | null;
  starting: DepositStarting;
  participantNumberEnabled: boolean;
  isMultiCurrencyInternalTransfer: boolean;
  hasAvailableCurrencies: boolean;
}): DepositFieldOptions => ({
  ...options,
  bankAccount: {
    ...options.bankAccount,
    requiredIf: requiredForTransferType(DEPOSIT_TRANSFER_TYPE.EFT),
  },
  fromSubAccount: {
    ...options.fromSubAccount,
    requiredIf: requiredForTransferType(
      DEPOSIT_TRANSFER_TYPE.INTERNAL_TRANSFER,
    ),
  },
  institution: {
    ...options.institution,
    requiredIf: requiredForTransferType(
      DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER,
    ),
  },
  accountNumber: {
    ...options.accountNumber,
    requiredIf: requiredForTransferType(
      DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER,
    ),
  },
  accountType: {
    ...options.accountType,
    requiredIf: requiredForTransferType(
      DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER,
    ),
  },
  transferType: {
    ...options.transferType,
    requiredIf: requiredForTransferType(
      DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER,
    ),
  },
  scheduledDate: {
    ...options.scheduledDate,
    requiredIf: (data: DepositData) =>
      data?.schedule !== Frequency.OneTime ||
      starting === DepositStarting.FutureDate,
  },
  ...(options.holdings
    ? {
        holdings: {
          ...options.holdings,
          requiredIf: requiredForTransferType(DEPOSIT_TRANSFER_TYPE.EFT),
        },
      }
    : {}),
  participantNumber:
    participantNumberEnabled &&
    applicableData?.type === DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER
      ? {
          enabled: true,
          required: FieldRequiredType.ERROR,
          requiredIf: requiredForTransferType(
            DEPOSIT_TRANSFER_TYPE.EXTERNAL_TRANSFER,
          ),
        }
      : {},
  currency: isMultiCurrencyInternalTransfer
    ? {
        enabled: true,
        required: FieldRequiredType.ERROR,
        requiredIf: () => hasAvailableCurrencies,
      }
    : {},
});
